import { useState, useRef, useCallback, useEffect } from 'react';
import { toast } from 'sonner';

export const useVoiceConversation = (presenceId, token) => {
  const [isConnected, setIsConnected] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [response, setResponse] = useState('');
  const [error, setError] = useState(null);
  
  const wsRef = useRef(null);
  const audioContextRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioElementRef = useRef(null);
  const audioSendTimerRef = useRef(null);
  const streamingChunksRef = useRef([]);
  
  // Get backend URL from env
  const getBackendWsUrl = () => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
    // Replace http/https with ws/wss
    let wsUrl = backendUrl.replace('http://', 'ws://').replace('https://', 'wss://');
    // Remove /api prefix if exists (it will be added back)
    wsUrl = wsUrl.replace('/api', '');
    return wsUrl;
  };

  const connect = useCallback(async (language = 'tr') => {
    try {
      // Check if already connected or connecting
      if (wsRef.current) {
        const state = wsRef.current.readyState;
        if (state === WebSocket.OPEN) {
          console.log('⚠️ WebSocket already connected, skipping...');
          return;
        } else if (state === WebSocket.CONNECTING) {
          console.log('⚠️ WebSocket already connecting, skipping...');
          return;
        }
      }
      
      // Convert full locale to language code (e.g., "en-US@posix" -> "en", "tr-TR" -> "tr")
      const languageCode = language.split('-')[0].split('@')[0];
      console.log('🌐 Language conversion:', language, '->', languageCode);
      
      const wsUrl = `${getBackendWsUrl()}/api/voice/${presenceId}`;
      console.log('🔌 Connecting to WebSocket:', wsUrl);
      const ws = new WebSocket(wsUrl);
      
      ws.onopen = () => {
        console.log('🔌 WebSocket connected');
        console.log('📤 Sending start message with token:', token ? 'YES' : 'NO', 'language:', languageCode);
        
        // Send start message with auth
        ws.send(JSON.stringify({
          type: 'start',
          token: token,
          language: languageCode
        }));
        
        console.log('✅ Start message sent');
      };
      
      ws.onmessage = async (event) => {
        const data = JSON.parse(event.data);
        console.log('📨 WebSocket message received:', data);
        
        switch (data.type) {
          case 'started':
            console.log('✅ Voice conversation started!', data);
            setIsConnected(true);
            toast.success(`Konuşma başladı: ${data.presence_name}`);
            break;
          
          case 'response':
            console.log('📝 Response received:', data);
            setTranscript(data.transcript);
            setResponse(data.response_text);
            
            // Play audio response
            if (data.audio) {
              await playAudio(data.audio);
            }
            break;
          
          case 'no_speech':
            console.log('🔇 No speech detected');
            setIsListening(false);
            break;
          
          case 'interrupt':
            console.log('⚠️ Interrupted');
            stopAudio();
            break;
          
          case 'interrupted':
            setIsSpeaking(false);
            break;
          
          case 'ended':
            console.log('🔚 Conversation ended');
            disconnect();
            break;
          
          case 'error':
            console.error('❌ Error:', data.message);
            setError(data.message);
            toast.error(data.message);
            break;
          
          default:
            console.log('Unknown message type:', data.type);
        }
      };
      
      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        setError('Connection error');
        toast.error('Bağlantı hatası');
      };
      
      ws.onclose = () => {
        console.log('🔌 WebSocket closed');
        setIsConnected(false);
        setIsListening(false);
        setIsSpeaking(false);
      };
      
      wsRef.current = ws;
      
    } catch (err) {
      console.error('Connection error:', err);
      setError(err.message);
      toast.error('Bağlantı kurulamadı');
    }
  }, [presenceId, token]);

  const disconnect = useCallback(() => {
    console.log('🔌 Disconnect called, current state:', wsRef.current?.readyState);
    
    if (wsRef.current) {
      const state = wsRef.current.readyState;
      
      if (state === WebSocket.OPEN) {
        console.log('📤 Sending end message and closing WebSocket...');
        try {
          wsRef.current.send(JSON.stringify({ type: 'end' }));
        } catch (e) {
          console.error('Error sending end message:', e);
        }
        wsRef.current.close();
        wsRef.current = null;
      } else if (state === WebSocket.CONNECTING) {
        console.log('⏳ WebSocket still connecting, skipping disconnect...');
        // Don't close while connecting - let it complete naturally
        // The connection will be closed on next disconnect call when OPEN
        return;
      } else if (state === WebSocket.CLOSING) {
        console.log('🔒 WebSocket already closing, waiting...');
        return;
      } else if (state === WebSocket.CLOSED) {
        console.log('🔒 WebSocket already closed');
        wsRef.current = null;
      }
    }
    
    stopRecording();
    stopAudio();
    
    setIsConnected(false);
    setIsListening(false);
    setIsSpeaking(false);
  }, []);

  const startRecording = useCallback(async () => {
    try {
      console.log('🎤 Requesting microphone access...');
      
      // Check if getUserMedia is supported
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('getUserMedia not supported in this browser');
      }
      
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });
      
      console.log('✅ Microphone access granted');
      
      // Check supported MIME types
      const mimeTypes = [
        'audio/webm;codecs=opus',
        'audio/webm',
        'audio/ogg;codecs=opus',
        'audio/mp4'
      ];
      
      let selectedMimeType = mimeTypes[0];
      for (const mimeType of mimeTypes) {
        if (MediaRecorder.isTypeSupported(mimeType)) {
          selectedMimeType = mimeType;
          console.log('✅ Using MIME type:', selectedMimeType);
          break;
        }
      }
      
      // Create MediaRecorder
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: selectedMimeType
      });
      
      audioChunksRef.current = [];
      
      mediaRecorder.ondataavailable = async (event) => {
        if (event.data.size > 0) {
          streamingChunksRef.current.push(event.data);
          console.log('📊 Audio chunk received:', event.data.size, 'bytes');
        }
      };
      
      // Send accumulated chunks every 3 seconds as final
      const sendAudioChunks = async () => {
        if (streamingChunksRef.current.length > 0) {
          // Combine all chunks
          const audioBlob = new Blob(streamingChunksRef.current, { type: selectedMimeType });
          console.log('📦 Combining', streamingChunksRef.current.length, 'chunks, total:', audioBlob.size, 'bytes');
          
          if (audioBlob.size > 1000) { // At least 1KB
            const reader = new FileReader();
            reader.onloadend = () => {
              const base64Audio = reader.result.split(',')[1];
              console.log('📤 Sending audio to server (final):', base64Audio.length, 'chars');
              
              if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
                wsRef.current.send(JSON.stringify({
                  type: 'audio',
                  data: base64Audio,
                  is_final: true // Send as final for processing
                }));
              }
            };
            reader.readAsDataURL(audioBlob);
            
            // Clear chunks after sending
            streamingChunksRef.current = [];
          }
        }
      };
      
      // Set interval to send chunks periodically
      audioSendTimerRef.current = setInterval(sendAudioChunks, 3000); // Every 3 seconds
      
      mediaRecorder.onstop = async () => {
        console.log('🛑 Recording stopped, processing audio...');
        
        // Send any remaining chunks
        await sendAudioChunks();
        
        // Combine chunks and send to server (this is for manual mode)
        const audioBlob = new Blob(streamingChunksRef.current, { type: selectedMimeType });
        console.log('📦 Audio blob created from stop:', audioBlob.size, 'bytes');
        
        // Only send if there's actual audio data
        if (audioBlob.size > 100) {
          // Convert to base64
          const reader = new FileReader();
          reader.onloadend = () => {
            const base64Audio = reader.result.split(',')[1];
            console.log('📤 Sending audio to server:', base64Audio.length, 'chars');
            
            if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
              wsRef.current.send(JSON.stringify({
                type: 'audio',
                data: base64Audio,
                is_final: true
              }));
            } else {
              console.error('❌ WebSocket not connected');
              toast.error('WebSocket bağlantısı yok');
            }
          };
          reader.readAsDataURL(audioBlob);
        } else {
          console.log('⚠️ Audio too short, not sending');
        }
        
        // Stop all tracks
        stream.getTracks().forEach(track => {
          track.stop();
          console.log('🔇 Audio track stopped');
        });
      };
      
      mediaRecorder.onerror = (event) => {
        console.error('❌ MediaRecorder error:', event.error);
        toast.error('Kayıt hatası: ' + event.error);
      };
      
      // Start recording with timeslice (1 second chunks for real-time streaming)
      mediaRecorder.start(1000); // Send chunk every 1 second
      mediaRecorderRef.current = mediaRecorder;
      setIsListening(true);
      
      console.log('🎤 Recording started successfully (streaming mode: 1s chunks)');
      toast.success('🎤 Kayıt başladı');
      
    } catch (err) {
      console.error('❌ Recording error:', err);
      
      let errorMessage = 'Mikrofon erişimi reddedildi';
      
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        errorMessage = 'Mikrofon izni verilmedi. Lütfen tarayıcı ayarlarınızdan mikrofon erişimine izin verin.';
      } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
        errorMessage = 'Mikrofon bulunamadı. Lütfen bir mikrofon bağlayın.';
      } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
        errorMessage = 'Mikrofon başka bir uygulama tarafından kullanılıyor.';
      } else if (err.name === 'OverconstrainedError') {
        errorMessage = 'Mikrofon ayarları desteklenmiyor.';
      } else if (err.name === 'SecurityError') {
        errorMessage = 'Güvenlik hatası: HTTPS gerekli olabilir.';
      }
      
      toast.error(errorMessage);
      setError(errorMessage);
    }
  }, []);

  const stopRecording = useCallback(() => {
    // Clear send timer
    if (audioSendTimerRef.current) {
      clearInterval(audioSendTimerRef.current);
      audioSendTimerRef.current = null;
    }
    
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
      setIsListening(false);
      console.log('🎤 Recording stopped');
    }
  }, []);

  const playAudio = useCallback(async (base64Audio) => {
    try {
      console.log('🔊 Playing audio response, length:', base64Audio.length);
      setIsSpeaking(true);
      
      // Decode base64 to blob
      const audioData = atob(base64Audio);
      console.log('📦 Decoded audio data:', audioData.length, 'bytes');
      
      const arrayBuffer = new ArrayBuffer(audioData.length);
      const view = new Uint8Array(arrayBuffer);
      
      for (let i = 0; i < audioData.length; i++) {
        view[i] = audioData.charCodeAt(i);
      }
      
      const blob = new Blob([arrayBuffer], { type: 'audio/mpeg' });
      const audioUrl = URL.createObjectURL(blob);
      console.log('🎵 Audio URL created:', audioUrl);
      
      // Create audio element
      const audio = new Audio(audioUrl);
      audioElementRef.current = audio;
      
      audio.onended = () => {
        console.log('✅ Audio playback ended');
        setIsSpeaking(false);
        URL.revokeObjectURL(audioUrl);
      };
      
      audio.onerror = (e) => {
        console.error('❌ Audio element error:', e);
        setIsSpeaking(false);
        URL.revokeObjectURL(audioUrl);
      };
      
      console.log('▶️ Starting audio playback...');
      await audio.play();
      console.log('✅ Audio playing');
      
    } catch (err) {
      console.error('❌ Audio playback error:', err);
      setIsSpeaking(false);
      toast.error('Ses oynatılamadı: ' + err.message);
    }
  }, []);

  const stopAudio = useCallback(() => {
    if (audioElementRef.current) {
      audioElementRef.current.pause();
      audioElementRef.current.currentTime = 0;
      setIsSpeaking(false);
    }
  }, []);

  const interrupt = useCallback(() => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'interrupt' }));
      stopAudio();
    }
  }, [stopAudio]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      disconnect();
    };
  }, [disconnect]);

  return {
    isConnected,
    isListening,
    isSpeaking,
    transcript,
    response,
    error,
    connect,
    disconnect,
    startRecording,
    stopRecording,
    interrupt
  };
};
